<template>
    <div class="hts-monitoring-vehicle__container">
        <div class="container-left show">
            <div class="flex-initial box-border p-2">
                <div class="w-full">
                    <hts-form-input
                        v-model="vehicleFilters.search"
                        class="w-full mt-1"
                        :placeholder="$t('labels.findVehicle')">
                        <template #prefix>
                            <hts-icons
                                icon-name="hi-magnifying-glass-icon"
                                class="text-gray-500" />
                        </template>
                    </hts-form-input>
                </div>
            </div>

            <div
                v-loading="vehicleLoading"
                class="content overflow-hidden">
                <div class="h-full overflow-auto pb-5">
                    <vehicle-list
                        :items="vehicles"
                        :filters="vehicleFilters"
                        @selected="handleSelectVehicle" />
                </div>
            </div>
        </div>

        <div class="container-right">
            <schedule-map
                v-if="showMap"
                :show-location="showMapLocation"
                :journey="selectedJourney" />

            <div class="journey">
                <div class="flex items-center justify-between">
                    <hts-form-input
                        v-model="journeyFilters.search"
                        class="w-72"
                        :placeholder="$t('labels.findJourney')">
                        <template #prefix>
                            <hi-magnifying-glass-icon class="h-6 w-6 ml-2 text-gray-500" />
                        </template>
                    </hts-form-input>
                    <div class="float-right flex gap-3">
                        <hts-map-icon-location store-key="distributing" />
                        <hts-map-icon-map store-key="distributing" />
                    </div>
                </div>
                <div class="mt-3">
                    <hts-data-table
                        ref="dataTable"
                        class="w-full"
                        :custom-style="{ 'min-height': '300px' }"
                        :headers="headers"
                        :server="server"
                        :query-extend="queryExtend"
                        row-selection="single"
                        enable-cell-change-flash
                        @selection-changed="handleJourneySelectedChange"
                        @row-click="handleRowClick" />
                </div>
            </div>
        </div>

        <journey-info
            ref="journeyInfoComponent"
            :exclude="['prices']"
            :api="$urlPartner.monitoring.journey.getDetail" />
        <alert-list-drawer ref="alertListComponent" />
    </div>
</template>

<script setup lang="ts">
import AlertListDrawer from './-components/alert-list-drawer.vue'
import VehicleList from './-components/vehicle-list.vue'
import JourneyInfo from '@/pages/trucker-[id]/-components/journey-info.vue'
import ScheduleMap from '@/pages/trucker-[id]/journey/-components/journey-schedule/map.vue'
import AlertStatus from '@/pages/trucker-[id]/monitoring/-components/alert-status.vue'
const { $event, $formatters } = useNuxtApp()
const { monitoringPage, map } = $store()
const company = computed(() => $store().system.workspaceCompany)
const dataTable = ref<any>()
const journeyInfoComponent: any = ref(null)
const alertListComponent: any = ref(null)
const alertNumber = ref(0)
const selectedJourney = ref<any>(undefined)
const server = $urlPartner.monitoring.journey.get
const queryExtend = computed(() => {
  return {
    vehicleId: selectedVehicleId.value,
    processAlertStatus: journeyFilters.value.processStatus,
    scheduleType: journeyFilters.value.scheduleType,
    vehicleType: journeyFilters.value.vehicleType,
    search: journeyFilters.value.search
  }
})

const vehicleLoading = ref(false)
const vehicles = ref<any[]>([])
const vehicleLength = computed(() => vehicles.value.length)
const vehicleFilters = computed({
  get: () => monitoringPage.vehicleFilters,
  set: (val: any) => {
    monitoringPage.setVehicleFilters(val)
  }
})
const journeyFilters = computed(() => monitoringPage.journeyFilters)

onBeforeMount(() => {
  eventListening()
})
onMounted(() => {
  const route = useRoute()
  if (route.query.journeyId) {
    openView({ _id: route.query.journeyId })
  }
  getVehicles()
})

const showMap = computed(() => {
  return map.getShowMap.distributing
})
const showMapLocation = computed(() => {
  return map.getShowLocation.distributing
})

onBeforeMount(() => {
  map.setShowLocation({ distributing: false })
  map.setShowMap({ distributing: true })
})

watch(
  () => showMap.value,
  (val: boolean) => {
    dataTable.value && dataTable.value.onResized()
  }
)

watch(
  () => vehicleFilters.value.search,
  (val: string) => {
    getVehiclesDebounce(true)
  }
)
watch(
  () => vehicleFilters.value.processStatus,
  (val: string) => {
    getVehiclesDebounce(true)
  }
)
watch(
  () => vehicleFilters.value.vehicleType,
  (val: string) => {
    getVehiclesDebounce(true)
  }
)
watch(
  () => journeyFilters.value.search,
  (val: string) => {}
)
watch(
  () => journeyFilters.value.processStatus,
  (val: any) => {
    refreshTable()
  }
)
watch(
  () => journeyFilters.value.scheduleType,
  (val: any) => {
    refreshTable()
  }
)
watch(
  () => journeyFilters.value.vehicleType,
  (val: any) => {
    refreshTable()
  }
)
watch(
  () => journeyFilters.value.search,
  (val: any) => {
    refreshTable()
  }
)

const refreshTable = $lodash.debounce(() => {
  dataTable.value && dataTable.value.refresh()
}, 500)

const getVehicles = (clear: boolean = false) => {
  vehicleLoading.value = true
  if (clear) {
    vehicles.value = []
  }
  $api($urlPartner.monitoring.vehicle.get, {
    body: {
      skip: vehicleLength.value,
      search: vehicleFilters.value.search,
      processStatus: vehicleFilters.value.processStatus,
      vehicleType: vehicleFilters.value.vehicleType
    }
  })
    .then(({ data }: any) => {
      const temp = (data?.value || {}).data || []
      if (Array.isArray(temp)) {
        vehicles.value = [...vehicles.value, ...temp]
      }
      vehicleLoading.value = false
    })
    .catch(() => {
      vehicleLoading.value = false
    })
}
const getVehiclesDebounce = $lodash.debounce(getVehicles, 500)

const eventListening = () => {
  $event.listen(`c-${company.value._id}-journey-change`, async (event: any) => {
    const { message } = event || {}
    const { journeyId, changes } = message || {}
    if (journeyId) {
      journeyInfoComponent.value && journeyInfoComponent.value.refresh(journeyId)
      dataTable.value && dataTable.value.setItem(journeyId, await getJourney(journeyId))
      alertNumber.value++
    }
  })
  $event.listen(`c-${company.value._id}-vehicle-change`, ({ id, type, location, journey, status, processStatus }: any) => {
    //
  })
}

const getJourney = async (id: string) => {
  const response = await $api($urlPartner.monitoring.journey.getDetail, { uriParams: { ID: id } })
  const { data } = response
  return (data?.value || {}).data || {}
}

const handleJourneySelectedChange = (journeys: any) => {
  const positions = journeys?.value?.[0]?.journey
  try {
    let check = true
    if (positions?.length) {
      if (!positions[0].lat || !positions[0].lng) {
        check = false
      }
      const origin = {
        lat: parseFloat(positions[0].lat),
        lng: parseFloat(positions[0].lng)
      }
      positions.splice(0, 1)
      if (!positions[positions.length - 1].lat || !positions[positions.length - 1].lng) {
        check = false
      }
      const destination = {
        lat: parseFloat(positions[positions.length - 1].lat),
        lng: parseFloat(positions[positions.length - 1].lng)
      }
      positions.splice(positions.length - 1, 1)
      const waypoints: { location: google.maps.LatLngLiteral }[] = []
      positions.forEach((position: any) => {
        if (!position.lat || !position.lng) {
          check = false
        }
        const waypoint = {
          location: {
            lat: parseFloat(position.lat),
            lng: parseFloat(position.lng)
          }
        }
        waypoints.push(waypoint)
      })
    }
  } catch (e) {}
}

const handleRowClick = (data: any) => {
  if (selectedJourney.value?._id === data._id) {
    return
  }
  selectedJourney.value = data
}

const openView = (row: any) => {
  journeyInfoComponent.value && journeyInfoComponent.value.show(row._id)
}

const selectedVehicleId = ref<any>(null)
const handleSelectVehicle = (vehicle: any) => {
  if (!vehicle || !vehicle._id) {
    selectedVehicleId.value = null
    refreshTable()
    return
  }
  selectedVehicleId.value = vehicle._id
  refreshTable()
}

const headers = ref([
  {
    field: 'processAlertStatus',
    headerName: '',
    sortable: false,
    lockPosition: true,
    lockPinned: true,
    suppressMenu: true,
    suppressMovable: true,
    resizable: false,
    minWidth: 40,
    maxWidth: 40,
    cellClass: 'cell-no-padding',
    cellRenderer: AlertStatus
  },
  {
    field: 'code',
    headerName: $t('labels.code'),
    sortable: true,
    filter: 'agTextColumnFilter'
  },
  {
    field: 'journeyName',
    headerName: $t('labels.journey2')
  },
  {
    field: 'transportation',
    sortable: true,
    headerName: $t('labels.transportation'),
    valueGetter: (params: any) => {
      const transportation = params?.data.orderData?.transportation
      if (transportation === 'CONTAINER') {
        return $t('labels.containerTruck')
      }
      if (transportation === 'NON_CONTAINER') {
        return $t('labels.nonContainerTruck')
      }
    }
  },
  {
    field: 'mergeContainer',
    headerName: $t('labels.mergeContainer'),
    valueGetter: (params: any) => {
      const mergeContainer = params?.data?.orderData?.mergeContainer
      if (mergeContainer) {
        return $t('labels.haveMergeContainer')
      }
      return $t('labels.notMergeContainer')
    }
  },
  {
    field: 'headContainer',
    headerName: $t('labels.headContainer'),
    valueGetter: (params: any) => {
      const headContainer = params?.data?.orderData?.headContainer
      if (headContainer) {
        return $t('labels.true')
      }
      return $t('labels.false')
    }
  },
  {
    field: 'commodityName',
    headerName: $t('labels.commodity'),
    valueGetter: (params: any) => {
      const commodityName = params?.data?.orderData?.commodityName
      const commodityCategory = params?.data?.orderData?.commodityCategory
      if (commodityName && commodityCategory) {
        return `${commodityName} - ${commodityCategory}`
      }
      if (commodityName) {
        return commodityName
      }
      return ''
    }
  },
  {
    field: 'etd',
    headerName: $t('labels.etd'),
    sortable: true,
    filter: 'agDateColumnFilter',
    valueGetter: (params: any) => {
      return $formatters.datetime(params.data.startAt)
    }
  },
  {
    field: 'eta',
    headerName: $t('labels.eta'),
    sortable: true,
    filter: 'agDateColumnFilter',
    valueGetter: (params: any) => {
      return $formatters.datetime(params.data.stopAt)
    }
  },
  {
    field: 'note',
    headerName: $t('labels.note')
  },
  {
    field: 'action',
    pinned: 'right',
    sortable: false,
    lockPosition: true,
    lockPinned: true,
    suppressMenu: true,
    suppressMovable: true,
    resizable: false,
    headerName: $t('labels.action'),
    headerClass: 'ag-header-text-center',
    cellStyle: { overflow: 'visible' },
    cellRenderer: 'DataTableActions',
    cellRendererParams: {
      storeKey: 'view',
      storeName: 'view',
      items: [
        {
          icon: 'hi-eye-icon',
          label: $t('labels.view'),
          class: 'text-blue-600 hover:text-blue-500',
          click: openView
        }
      ]
    }
  }
])
</script>
